import { FC } from 'react';

import LinkIcon from '@mui/icons-material/Link';
import { Box } from '@mui/material';

import { navigate, routes } from '@redwoodjs/router';

import HeadingLabeledValueCard from 'src/components/HeadingLabeledValueCard';
import { RiskCard } from 'src/pages/AddMerchantPage/steps/SummaryInformation/components/RisksSection/components/RiskCard';

interface DisplayConfig {
    showMerchant: boolean;
    showMerchantAccountNumber: boolean;
    showMID: boolean;
    showPartner: boolean;
    enableMerchantLink: boolean;
    enablePartnerLink: boolean;
}

interface Props {
    tradingName: string;
    merchantAccountNumber: string;
    mid: string;
    partnerName: string;
    partnerId: number;
    height: number | string;
    displayConfig?: DisplayConfig;
}

const MerchantInfoColumn: FC<Props> = ({
    tradingName,
    merchantAccountNumber,
    mid,
    partnerName,
    partnerId,
    height,
    displayConfig = {
        enableMerchantLink: true,
        enablePartnerLink: true,
        showMerchant: true,
        showMerchantAccountNumber: true,
        showMID: true,
        showPartner: true,
    },
}) => {
    const {
        showMerchant,
        showMerchantAccountNumber,
        showMID,
        showPartner,
        enableMerchantLink,
        enablePartnerLink,
    } = displayConfig;

    return (
        <Box
            flex="0 0 26%"
            height={height}
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{
                '& > *': {
                    flexGrow: 1,
                    flexShrink: 1,
                    minHeight: 0,
                },
            }}
        >
            {/* Merchant */}
            {showMerchant && (
                <Box
                    flexGrow={1}
                    justifyContent="center"
                    onClick={() => {
                        if (!enableMerchantLink) return null;
                        return navigate(routes.viewMerchant({ merchantAccountNumber }));
                    }}
                    sx={
                        enableMerchantLink
                            ? { cursor: 'pointer', display: 'flex' }
                            : { display: 'flex' }
                    }
                >
                    <RiskCard
                        label="Merchant Name"
                        value={
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={
                                    enableMerchantLink
                                        ? {
                                              cursor: 'pointer',
                                              '&:hover': {
                                                  color: 'primary.main',
                                              },
                                          }
                                        : {}
                                }
                            >
                                {enableMerchantLink && <LinkIcon sx={{ fontSize: 16, mr: 0.5 }} />}
                                {tradingName}
                            </Box>
                        }
                    />
                </Box>
            )}

            {/* Merchant Account Number */}
            {showMerchantAccountNumber && (
                <Box flexGrow={1} display="flex" justifyContent="center">
                    <RiskCard
                        label="Merchant Account Number"
                        value={merchantAccountNumber}
                        valueStyle={{ whiteSpace: 'pre-line' }}
                    />
                </Box>
            )}

            {/* MID */}
            {showMID && (
                <Box display="flex" flexGrow={1} justifyContent="center">
                    <HeadingLabeledValueCard title="MID" content={mid} />
                </Box>
            )}

            {/* Partner */}
            {showPartner && (
                <Box
                    flexGrow={1}
                    onClick={() => {
                        if (!enablePartnerLink) return null;
                        return navigate(
                            routes.viewPartner({
                                id: partnerId,
                                partnerOfPartner: false,
                            })
                        );
                    }}
                    sx={
                        enablePartnerLink
                            ? { cursor: 'pointer', display: 'flex' }
                            : { display: 'flex' }
                    }
                >
                    <RiskCard
                        label="Partner Name"
                        value={
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={
                                    enablePartnerLink
                                        ? {
                                              cursor: 'pointer',
                                              '&:hover': {
                                                  color: 'primary.main',
                                              },
                                          }
                                        : {}
                                }
                            >
                                {enablePartnerLink && <LinkIcon sx={{ fontSize: 16, mr: 0.5 }} />}
                                {partnerName}
                            </Box>
                        }
                    />
                </Box>
            )}
        </Box>
    );
};

export default MerchantInfoColumn;
